/**
 * Peer Group editor (tabbed modal).
 *
 * Replaces the previous ~560-line hand-rolled modal in PeerGroupsPage with the
 * shared ResourceEditor. Six tabs: General, Capacity, Failover, Media,
 * Keepalive, Metadata. The form is UI-shaped (number/CSV/header inputs are
 * strings); on submit it transforms to the typed payload and validates with the
 * core PeerGroup schemas before calling the mutation hooks.
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Settings, Scale, GitBranch, Network, Tags } from 'lucide-react';
import { PeerGroupCreateInputSchema, PeerGroupUpdateInputSchema } from '@sbclite/core/schemas';
import { useCreatePeerGroup, useUpdatePeerGroup, usePeerGroups, type PeerGroupWithPeers } from '../../hooks/usePeerGroups';
import { useMediaRealms } from '../../hooks/useMediaRealms';
import { useSipInterfaces } from '../../hooks/useSipInterfaces';
import { ResourceEditor, firstTabWithError, describeSubmitError, type ResourceEditorTab } from '../common';
import { NotYetEnforcedNotice } from '../common/badges';

// ========================================
// Transform helpers (string form fields -> typed payload)
// ========================================
function optionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function optionalText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function parseCsv(value: string): string[] | undefined {
  const values = value.split(',').map((entry) => entry.trim()).filter(Boolean);
  return values.length === 0 ? undefined : values;
}

function parseHeaderLines(value: string): string[] {
  const headers: string[] = [];
  for (const line of value.split('\n')) {
    const trimmed = line.trim();
    if (trimmed === '') continue;
    const separator = trimmed.indexOf(':');
    if (separator <= 0) continue;
    const name = trimmed.slice(0, separator).trim();
    const headerValue = trimmed.slice(separator + 1).trim();
    if (name !== '' && headerValue !== '') headers.push(`${name}: ${headerValue}`);
  }
  return headers;
}

function numberText(value: unknown): string {
  return typeof value === 'number' ? String(value) : '';
}

function boolFrom(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

// ========================================
// Form schema (UI-shaped)
// ========================================
const groupTypeEnum = z.enum(['peer_group', 'trunk_group', 'carrier_group', 'teams_group', 'emergency_group']);
const loadBalancingEnum = z.enum(['round_robin', 'weighted', 'least_calls']);
const selectionModeEnum = z.enum(['failover', 'parallel']);
const peerGroupFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  enabled: z.boolean(),
  healthCheckEnabled: z.boolean(),
  groupType: groupTypeEnum,
  loadBalancingStrategy: loadBalancingEnum,
  selectionMode: selectionModeEnum,
  maxConcurrentCalls: z.string(),
  minActiveMembers: z.string(),
  maxCallsPerPeer: z.string(),
  affinityTimeout: z.string(),
  hotSwapMode: z.boolean(),
  sessionAffinity: z.boolean(),
  autoFailover: z.boolean(),
  failoverRetries: z.string(),
  failoverDelay: z.string(),
  backupGroupId: z
    .string()
    .refine((v) => v.trim() === '' || z.string().uuid().safeParse(v.trim()).success, {
      message: 'Must be a valid UUID (or empty)',
    }),
  mediaRealmId: z.string(),
  sipInterface: z.string(),
  tlsContextName: z.string(),
  customHeadersText: z.string(),
  tags: z.string(),
  environment: z.string(),
  carrierId: z.string(),
  carrierContractId: z.string(),
  carrierSupportContact: z.string(),
  teamsTenantId: z.string(),
  teamsDirectRoutingDomains: z.string(),
});

type PeerGroupFormData = z.infer<typeof peerGroupFormSchema>;

function buildDefaultValues(group?: PeerGroupWithPeers | null): PeerGroupFormData {
  return {
    name: group?.name || '',
    description: group?.description || '',
    enabled: group?.enabled ?? true,
    healthCheckEnabled: group?.healthCheckEnabled ?? true,
    groupType: group?.groupType || 'peer_group',
    loadBalancingStrategy: group?.loadBalancingStrategy || 'round_robin',
    selectionMode: group?.selectionMode || 'failover',
    maxConcurrentCalls: group?.maxConcurrentCalls != null ? String(group.maxConcurrentCalls) : '',
    minActiveMembers: numberText(group?.minActiveMembers),
    maxCallsPerPeer: numberText(group?.maxCallsPerPeer),
    affinityTimeout: numberText(group?.affinityTimeoutSeconds),
    hotSwapMode: boolFrom(group?.hotSwapMode),
    sessionAffinity: boolFrom(group?.sessionAffinity),
    autoFailover: boolFrom(group?.autoFailover),
    failoverRetries: numberText(group?.failoverRetries),
    failoverDelay: numberText(group?.failoverDelayMs),
    backupGroupId: group?.backupGroupId || '',
    mediaRealmId: group?.mediaRealmId || '',
    sipInterface: group?.preferredSipInterfaceId || '',
    tlsContextName: group?.tlsContextName || '',
    customHeadersText: group?.customHeaderLines?.join('\n') ?? '',
    tags: group?.tags?.join(', ') ?? '',
    environment: group?.environment || '',
    carrierId: group?.carrierId || '',
    carrierContractId: group?.carrierContractId || '',
    carrierSupportContact: group?.carrierSupportContact || '',
    teamsTenantId: group?.teamsTenantId || '',
    teamsDirectRoutingDomains: group?.teamsDirectRoutingDomains?.join(', ') ?? '',
  };
}

function toPayload(data: PeerGroupFormData) {
  return {
    name: data.name,
    description: data.description || undefined,
    enabled: data.enabled,
    groupType: data.groupType,
    selectionMode: data.selectionMode,
    loadBalancingStrategy: data.loadBalancingStrategy,
    maxConcurrentCalls: optionalNumber(data.maxConcurrentCalls),
    healthCheckEnabled: data.healthCheckEnabled,
    minActiveMembers: optionalNumber(data.minActiveMembers) ?? null,
    maxCallsPerPeer: optionalNumber(data.maxCallsPerPeer) ?? null,
    hotSwapMode: data.hotSwapMode,
    sessionAffinity: data.sessionAffinity,
    affinityTimeoutSeconds: optionalNumber(data.affinityTimeout) ?? null,
    autoFailover: data.autoFailover,
    failoverRetries: optionalNumber(data.failoverRetries) ?? null,
    failoverDelayMs: optionalNumber(data.failoverDelay) ?? null,
    backupGroupId: optionalText(data.backupGroupId) ?? null,
    mediaRealmId: optionalText(data.mediaRealmId) ?? null,
    preferredSipInterfaceId: optionalText(data.sipInterface) ?? null,
    tlsContextName: optionalText(data.tlsContextName) ?? null,
    customHeaderLines: parseHeaderLines(data.customHeadersText),
    tags: parseCsv(data.tags) ?? [],
    environment: optionalText(data.environment) ?? null,
    carrierId: optionalText(data.carrierId) ?? null,
    carrierContractId: optionalText(data.carrierContractId) ?? null,
    carrierSupportContact: optionalText(data.carrierSupportContact) ?? null,
    teamsTenantId: optionalText(data.teamsTenantId) ?? null,
    teamsDirectRoutingDomains: parseCsv(data.teamsDirectRoutingDomains) ?? [],
  };
}

interface PeerGroupEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingGroup?: PeerGroupWithPeers | null;
}

export function PeerGroupEditor({ isOpen, onClose, onSuccess, editingGroup }: PeerGroupEditorProps) {
  const isEditing = Boolean(editingGroup);
  const [activeTab, setActiveTab] = useState('general');
  const [error, setError] = useState<string | null>(null);

  const form = useForm<PeerGroupFormData>({
    resolver: zodResolver(peerGroupFormSchema),
    defaultValues: buildDefaultValues(editingGroup),
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildDefaultValues(editingGroup));
    setActiveTab('general');
    setError(null);
  }, [isOpen, editingGroup, form]);

  const createMutation = useCreatePeerGroup();
  const updateMutation = useUpdatePeerGroup();

  const onSubmit = async (data: PeerGroupFormData) => {
    setError(null);
    try {
      const payload = toPayload(data);
      if (isEditing && editingGroup) {
        const parsed = PeerGroupUpdateInputSchema.parse(payload);
        await updateMutation.mutateAsync({ groupId: editingGroup.id, data: parsed });
      } else {
        const parsed = PeerGroupCreateInputSchema.parse(payload);
        await createMutation.mutateAsync(parsed);
      }
      onSuccess?.();
      onClose();
      form.reset(buildDefaultValues(null));
      setActiveTab('general');
    } catch (err) {
      setError(describeSubmitError(err, 'Failed to save peer group'));
    }
  };

  const tabs: ResourceEditorTab[] = useMemo(
    () => [
      {
        id: 'general',
        label: 'General',
        icon: Settings,
        fields: ['name', 'description', 'groupType', 'loadBalancingStrategy', 'selectionMode', 'maxConcurrentCalls', 'enabled', 'healthCheckEnabled'],
        render: () => <GeneralTab form={form} isEditing={isEditing} />,
      },
      {
        id: 'capacity',
        label: 'Capacity',
        icon: Scale,
        fields: ['minActiveMembers', 'maxCallsPerPeer', 'affinityTimeout', 'hotSwapMode', 'sessionAffinity'],
        render: () => <CapacityTab form={form} />,
      },
      {
        id: 'failover',
        label: 'Failover',
        icon: GitBranch,
        fields: ['autoFailover', 'failoverRetries', 'failoverDelay', 'backupGroupId'],
        render: () => <FailoverTab form={form} editingGroupId={editingGroup?.id} />,
      },
      {
        id: 'media',
        label: 'Media & Interface',
        icon: Network,
        fields: ['mediaRealmId', 'sipInterface', 'tlsContextName', 'customHeadersText'],
        render: () => <MediaTab form={form} />,
      },
      {
        id: 'metadata',
        label: 'Metadata',
        icon: Tags,
        fields: ['tags', 'environment', 'carrierId', 'carrierContractId', 'carrierSupportContact', 'teamsTenantId', 'teamsDirectRoutingDomains'],
        render: () => <MetadataTab form={form} />,
      },
    ],
    [form, isEditing, editingGroup]
  );

  const onInvalid = (errors: typeof form.formState.errors) => {
    const target = firstTabWithError(errors, tabs);
    if (target) setActiveTab(target);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <ResourceEditor
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Peer Group' : 'Create Peer Group'}
      subtitle={isEditing ? 'Update peer group configuration' : 'Create a new peer group for load balancing'}
      headerBanner={
        error ? (
          <div className="bg-red-50 px-6 py-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        ) : undefined
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      errors={form.formState.errors}
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      onCancel={onClose}
      isSaving={isSaving}
      saveLabel={isEditing ? 'Save Changes' : 'Create Group'}
      savingLabel="Saving..."
    />
  );
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function GeneralTab({ form, isEditing }: { form: UseFormReturn<PeerGroupFormData>; isEditing: boolean }) {
  return (
    <div className="space-y-4">
      <div>
        <label className={labelClass}>Name *</label>
        <input type="text" {...form.register('name')} className={inputClass} placeholder="My Peer Group" disabled={isEditing} />
        {form.formState.errors.name && <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>}
        {isEditing && <p className="text-xs text-gray-500 mt-1">Name cannot be changed after creation</p>}
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea {...form.register('description')} rows={2} className={inputClass} placeholder="Optional description" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            Group Type <NotYetEnforcedNotice />
          </label>
          <select {...form.register('groupType')} className={inputClass}>
            <option value="peer_group">Peer Group</option>
            <option value="trunk_group">Trunk Group</option>
            <option value="carrier_group">Carrier Group</option>
            <option value="teams_group">Teams Group</option>
            <option value="emergency_group">Emergency Group</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>
            Load Balancing
          </label>
          <select {...form.register('loadBalancingStrategy')} className={inputClass}>
            <option value="round_robin">Round Robin</option>
            <option value="weighted">Weighted</option>
            <option value="least_calls">Least Calls</option>
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>Selection Mode</label>
        <select {...form.register('selectionMode')} className={inputClass}>
          <option value="failover">Failover - choose one peer at a time</option>
          <option value="parallel">Parallel Fork - ring active priority peers together</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Use Parallel Fork to ring all enabled peers in the best priority tier at the same time.
        </p>
      </div>

      <div>
        <label className={labelClass}>
          Max Concurrent Calls
        </label>
        <input type="number" {...form.register('maxConcurrentCalls')} className={inputClass} placeholder="1000" min="1" max="100000" />
      </div>

      <div className="flex items-center gap-6 pt-2">
        <label className="flex items-center gap-2">
          <input type="checkbox" {...form.register('enabled')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="text-sm text-gray-700">Enabled</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" {...form.register('healthCheckEnabled')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="text-sm text-gray-700">Health Check</span>
        </label>
      </div>
    </div>
  );
}

function CapacityTab({ form }: { form: UseFormReturn<PeerGroupFormData> }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Routing Capacity</h3>
        <p className="text-xs text-gray-500">Group-level thresholds used by routing and failover policy.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Minimum Active Members <NotYetEnforcedNotice /></label>
          <input type="number" {...form.register('minActiveMembers')} className={inputClass} min="0" />
        </div>
        <div>
          <label className={labelClass}>Max Calls Per Peer</label>
          <input type="number" {...form.register('maxCallsPerPeer')} className={inputClass} min="0" />
        </div>
        <div>
          <label className={labelClass}>Affinity Timeout (seconds) <NotYetEnforcedNotice /></label>
          <input type="number" {...form.register('affinityTimeout')} className={inputClass} min="0" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2">
          <input type="checkbox" {...form.register('hotSwapMode')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="text-sm text-gray-700">Hot swap failed peers <NotYetEnforcedNotice /></span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" {...form.register('sessionAffinity')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="text-sm text-gray-700">Session affinity <NotYetEnforcedNotice /></span>
        </label>
      </div>
    </div>
  );
}

function FailoverTab({
  form,
  editingGroupId,
}: { form: UseFormReturn<PeerGroupFormData>; editingGroupId?: string }) {
  const { data: groupsResponse } = usePeerGroups();
  const groups = groupsResponse?.data ?? [];
  // Exclude the group being edited to prevent self-referential backup loops.
  const backupOptions = groups.filter((group) => group.id !== editingGroupId);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Failover</h3>
        <p className="text-xs text-gray-500">Backup group is used when the primary group has no usable targets. Retry orchestration fields are not enforced yet.</p>
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" {...form.register('autoFailover')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
        <span className="text-sm text-gray-700">Auto failover <NotYetEnforcedNotice /></span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Retries <NotYetEnforcedNotice /></label>
          <input type="number" {...form.register('failoverRetries')} className={inputClass} min="0" />
        </div>
        <div>
          <label className={labelClass}>Delay (ms) <NotYetEnforcedNotice /></label>
          <input type="number" {...form.register('failoverDelay')} className={inputClass} min="0" />
        </div>
        <div>
          <label className={labelClass}>Backup Group</label>
          <select {...form.register('backupGroupId')} className={inputClass}>
            <option value="">None</option>
            {backupOptions.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} {!group.enabled ? '(disabled)' : ''}
              </option>
            ))}
          </select>
          {form.formState.errors.backupGroupId && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.backupGroupId.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MediaTab({ form }: { form: UseFormReturn<PeerGroupFormData> }) {
  const { data: mediaRealms = [] } = useMediaRealms();
  const { data: sipInterfaces = [] } = useSipInterfaces();
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Media And SIP Interface</h3>
          <p className="text-xs text-gray-500">Optional group-level defaults for media and signaling.</p>
        </div>
        <NotYetEnforcedNotice />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Media Realm</label>
          <select {...form.register('mediaRealmId')} className={inputClass}>
            <option value="">No group default</option>
            {mediaRealms.map((realm) => (
              <option key={realm.id} value={realm.id}>{realm.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Preferred SIP Interface</label>
          <select {...form.register('sipInterface')} className={inputClass}>
            <option value="">No group default</option>
            {sipInterfaces.map((iface) => (
              <option key={iface.id} value={iface.id}>
                {iface.interfaceId} ({iface.protocol}:{iface.port})
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass}>TLS Context Name</label>
        <input type="text" {...form.register('tlsContextName')} className={inputClass} placeholder="Optional TLS context" />
      </div>
      <div>
        <label className={labelClass}>Custom Headers</label>
        <textarea
          {...form.register('customHeadersText')}
          rows={3}
          className={`${inputClass} font-mono text-sm`}
          placeholder={'X-Customer: acme\nX-Route-Class: premium'}
        />
        <p className="text-xs text-gray-500 mt-1">One header per line as Name: value.</p>
      </div>
    </div>
  );
}

function MetadataTab({ form }: { form: UseFormReturn<PeerGroupFormData> }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Metadata</h3>
        <p className="text-xs text-gray-500">Operational labels and carrier/Teams annotations.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Tags</label>
          <input type="text" {...form.register('tags')} className={inputClass} placeholder="production, premium" />
        </div>
        <div>
          <label className={labelClass}>Environment</label>
          <input type="text" {...form.register('environment')} className={inputClass} placeholder="production" />
        </div>
        <div>
          <label className={labelClass}>Carrier ID</label>
          <input type="text" {...form.register('carrierId')} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Carrier Contract ID</label>
          <input type="text" {...form.register('carrierContractId')} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Carrier Support Contact</label>
          <input type="text" {...form.register('carrierSupportContact')} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Teams Tenant ID</label>
          <input type="text" {...form.register('teamsTenantId')} className={inputClass} />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Teams Direct Routing Domains</label>
          <input type="text" {...form.register('teamsDirectRoutingDomains')} className={inputClass} placeholder="sbc1.example.com, sbc2.example.com" />
        </div>
      </div>
    </div>
  );
}

export default PeerGroupEditor;
